// frontend/src/services/auth/tokenService.js
import api from '../api';

// Token storage key
const TOKEN_KEY = 'auth_token';

/**
 * Get the authentication token from storage
 * @returns {string|null} - The stored token or null if not found
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set the authentication token in storage and API headers
 * @param {string} token - The token to store
 */
export function setToken(token) {
  if (!token) {
    console.error('Attempted to set empty token');
    return;
  }
  
  // Store token in localStorage
  localStorage.setItem(TOKEN_KEY, token);
  
  // Set token in API headers
  if (api.defaults.headers) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
}

/**
 * Remove the authentication token from storage and API headers
 */
export function removeToken() {
  // Remove from localStorage
  localStorage.removeItem(TOKEN_KEY);
  
  // Remove from API headers
  if (api.defaults.headers) {
    delete api.defaults.headers.common['Authorization'];
  }
}

/**
 * Parse JWT token to get payload
 * @param {string} token - The JWT token
 * @returns {Object|null} - Decoded payload or null if invalid
 */
export function parseToken(token) {
  if (!token) return null;
  
  try {
    // Split the token and get the payload part
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the base64 payload
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    
    // Parse the JSON payload
    return JSON.parse(payload);
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param {string} token - The JWT token
 * @returns {boolean} - True if expired or invalid, false otherwise
 */
export function isTokenExpired(token) {
  if (!token) return true;
  
  const payload = parseToken(token);
  if (!payload || !payload.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  // Consider token expired 5 minutes before actual expiration
  const safetyBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  return currentTime >= (expirationTime - safetyBuffer);
}

/**
 * Get time until token expiration
 * @param {string} token - The JWT token
 * @returns {number} - Milliseconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token) {
  if (!token) return 0;
  
  const payload = parseToken(token);
  if (!payload || !payload.exp) return 0;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  return Math.max(0, expirationTime - currentTime);
}

export default {
  getToken,
  setToken,
  removeToken,
  parseToken,
  isTokenExpired,
  getTimeUntilExpiration
};